import { useEffect, useMemo, useState } from "react";
import { Card, ConfirmDialog, EmptyState, Spinner } from "../../shared/ui/index.js";
import { MediaTile } from "../media/MediaTile.js";
import { useDeleteMedia, useOwnerGallery } from "./events.api.js";
import { plural } from "../../shared/format.js";
import { useToast } from "../../shared/toast.js";

export function GalleryPanel({ eventId }: { eventId: string }) {
  const gallery = useOwnerGallery(eventId);
  const del = useDeleteMedia(eventId);
  const toast = useToast();
  const items = useMemo(() => gallery.data?.pages.flatMap((p) => p.items) ?? [], [gallery.data]);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [guestFilter, setGuestFilter] = useState("");
  const target = items.find((m) => m.id === confirmId);

  /* La galerie doit rester complète (filtre invité, zip) : on enchaîne les pages. */
  useEffect(() => {
    if (gallery.hasNextPage && !gallery.isFetchingNextPage) {
      gallery.fetchNextPage();
    }
  }, [gallery.hasNextPage, gallery.isFetchingNextPage, gallery.fetchNextPage]);

  const guestOptions = useMemo(() => {
    const map = new Map<string, { key: string; label: string; count: number }>();
    for (const m of items) {
      const key = m.email ?? m.pseudo;
      const existing = map.get(key);
      if (existing) existing.count++;
      else map.set(key, { key, label: m.pseudo, count: 1 });
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [items]);

  const visible = guestFilter
    ? items.filter((m) => (m.email ?? m.pseudo) === guestFilter)
    : items;

  const zipHref = guestFilter
    ? `/api/events/${eventId}/download?guestEmail=${encodeURIComponent(guestFilter)}`
    : `/api/events/${eventId}/download`;

  return (
    <Card id="gallery">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif text-lg">
          Galerie ({items.length} {plural(items.length, "média")})
        </h2>
        {items.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {guestOptions.length > 1 && (
              <select
                value={guestFilter}
                onChange={(e) => setGuestFilter(e.target.value)}
                className="rounded-full border border-stone-300 px-3 py-2 text-sm"
              >
                <option value="">Tous les invités</option>
                {guestOptions.map((g) => (
                  <option key={g.key} value={g.key}>
                    {g.label} ({g.count})
                  </option>
                ))}
              </select>
            )}
            <a
              href={zipHref}
              className="rounded-full border border-stone-300 px-4 py-2 text-sm"
            >
              {guestFilter ? "Télécharger ce dossier (.zip)" : "Tout télécharger (.zip)"}
            </a>
          </div>
        )}
      </div>

      {gallery.isLoading ? (
        <div className="mt-4">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <div className="mt-4">
          <EmptyState>Aucun média pour l'instant.</EmptyState>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {visible.map((m) => (
            <MediaTile
              key={m.id}
              item={m}
              onDelete={() => setConfirmId(m.id)}
              downloadHref={`/api/media/${m.id}/download`}
            />
          ))}
        </div>
      )}

      {target && (
        <ConfirmDialog
          title="Supprimer ce média ?"
          body={`${target.kind === "video" ? "La vidéo" : "La photo"} de ${target.pseudo} sera définitivement supprimée (original inclus). Cette action est irréversible.`}
          confirmLabel="Supprimer définitivement"
          onConfirm={() =>
            del.mutate(target.id, {
              onSuccess: () => toast("Média supprimé"),
              onError: () => toast("Échec de la suppression", "err"),
            })
          }
          onClose={() => setConfirmId(null)}
        />
      )}
    </Card>
  );
}
